import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession, useHasWorkspace } from '../../lib/auth/session.js';
import { useCdrs, usePhonebookLookup } from '../../lib/api/hooks.js';
import { api } from '../../lib/api/client.js';
import { Badge, Empty, SectionHead, CardHead, Tabs, Tab } from '../../components/ui/Primitives.js';
import { Link } from '@tanstack/react-router';
import { Icon } from '../../components/ui/Icon.js';
import { Modal } from '../../components/ui/Modal.js';
import type { BrowserPhoneApi, PhoneCall } from './useBrowserPhone.js';
import type { ShiftApi } from './useShift.js';
import { usePhone } from './PhoneProvider.js';
import { WrapUp } from './WrapUp.js';
import { useMessages } from './messages.js';
import { CampaignPanel } from './CampaignPanel.js';
import { CallWorkModal } from './CallWorkModal.js';
import { useColleagues, useBookContacts, useSearch, STATE_LABEL, STATE_TONE, type Colleague, type Hit } from './directory.js';
import { relativeTime, formatDuration } from '../../lib/format.js';
import type { AgentAttendance, Cdr, Extension, PhonebookContact, PhonebookMatch, WorkspaceControls } from '@arcnet/contracts';

type Book = PhonebookContact[] | undefined;

/**
 * The agent workspace (docs/36 §8): one screen a person works a shift in, with the phone in it.
 *
 * Redesigned 3 October 2026 with the owner, on the pattern contact-centre desktops share (Aircall,
 * Talkdesk, Genesys): in the console's frame; a status bar with the person's state as one choice
 * (Ready, or a pause with its reason), their shift, and one Log in / Log out that starts the phone
 * with it; the call as the centre of the page; the dial pad -- which finds colleagues and contacts
 * as you type, by name or keypad letters -- and the colleagues with what each is doing, live, on
 * the right; their day and their calls underneath. Nothing to call with until they log in.
 *
 * The phone and the shift are the portal's (PhoneProvider), so leaving this page does not hang up.
 */
export function AgentWorkspace() {
  const tenantId = useSession((s) => s.activeTenantId);
  const hasWorkspace = useHasWorkspace();
  const ctx = usePhone();

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected" hint="Choose an account to take calls on." />;
  }
  // For somebody who arrives at the address without a seat that takes calls in the browser.
  if (!hasWorkspace) {
    return (
      <>
        <SectionHead title="Workspace" />
        <div className="arc-card">
          <Empty icon="headset" title="The workspace is not for this extension"
            hint="Taking calls in the browser comes with the agent packages. Ask your provider to add it, and this screen opens." />
        </div>
      </>
    );
  }

  const seat = ctx.seat;
  return (
    <>
      <SectionHead title="Workspace"
        sub={seat ? `${seat.number} · ${seat.display_name}` : 'Choose the extension you are taking calls on.'}
        action={ctx.choices.length > 1 && (
          <select className="arc-select" style={{ width: 240 }} value={seat?.id ?? ''} aria-label="Extension"
            disabled={!!ctx.phone?.call} onChange={(e) => ctx.choose(e.target.value || null)}>
            <option value="">Choose an extension…</option>
            {ctx.choices.map((e) => <option key={e.id} value={e.id}>{e.number} · {e.display_name}</option>)}
          </select>
        )} />
      {!seat || !ctx.phone || !ctx.shift ? (
        <div className="arc-card">
          {ctx.loading
            ? <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 80 }} /></div>
            : ctx.choices.length === 0
              ? <Empty icon="extension" title="No extension" hint="The workspace needs an extension that takes calls in the browser." />
              : <Empty icon="headset" title="Choose an extension" hint="Pick the extension you are taking calls on, above." />}
        </div>
      ) : <Seat tenantId={tenantId} seat={seat} phone={ctx.phone} shift={ctx.shift} endedAt={ctx.endedAt} />}
    </>
  );
}

/** Midnight today, local, for "today" on this screen. */
const todayStart = (): string => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); };

function Seat({ tenantId, seat, phone, shift, endedAt }: {
  tenantId: string; seat: Extension; phone: BrowserPhoneApi; shift: ShiftApi; endedAt: number;
}) {
  const { inCall } = usePhone();
  const extensionId = seat.id;
  const calls = useCdrs(tenantId, { extension_id: extensionId }, 25);
  useEffect(() => { if (inCall?.saved === 'saved') void calls.refetch(); }, [inCall?.saved]); // eslint-disable-line react-hooks/exhaustive-deps
  const [from] = useState(todayStart);
  const today = useCdrs(tenantId, { extension_id: extensionId, from }, 200);
  useEffect(() => {
    if (endedAt) { void calls.refetch(); void today.refetch(); }
  }, [endedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => calls.data?.pages.flatMap((p) => p.data) ?? [], [calls.data]);
  // Who the numbers on this screen belong to: the call now and every row behind it, in one request.
  const numbers = useMemo(() => {
    const out = rows.map((c) => (c.direction === 'outbound' ? c.to_number : c.from_number) ?? '');
    if (phone.call) out.push(phone.call.number);
    if (phone.consult) out.push(phone.consult.number);
    return out;
  }, [rows, phone.call, phone.consult]);
  const contacts = usePhonebookLookup(tenantId, numbers).data?.data ?? {};
  const colleagues = useColleagues(tenantId, extensionId).data?.data;
  const book = useBookContacts(tenantId, extensionId, seat.admin_group_id ?? null).data;
  // A campaign lead asks its own "how did it go" (CampaignPanel), so the general wrap-up steps aside.
  const [campaignActive, setCampaignActive] = useState(false);
  const [lower, setLower] = useState<'calls' | 'day'>('calls');
  const [openCall, setOpenCall] = useState<string | null>(null);
  // The caller the panel is about: the call now, or the last one until closed or replaced.
  const [panelNumber, setPanelNumber] = useState<string | null>(null);
  useEffect(() => { if (phone.call?.number) setPanelNumber(phone.call.number); }, [phone.call?.number]);
  const tz = useSession((s) => s.session?.user.timezone) ?? 'Africa/Johannesburg';
  const onCampaignActive = useCallback((v: boolean) => setCampaignActive(v), []);
  /** Logged on, with the phone up: until then there is nothing to call with, and it looks it. */
  const live = !!shift.shiftId && phone.state === 'ready';
  const ctl = shift.controls;
  // Not while wrapping up either: the call just finished is marked before another is started.
  const canDial = live && !phone.call && ctl.manual_dial && shift.state !== 'wrap_up';
  const nameOf = (n: string) => contacts[n]?.name ?? colleagues?.find((c) => c.number === n)?.name;

  return (
    <div className="arc-ws">
      {openCall && (
        <CallWorkModal tenantId={tenantId} callId={openCall} tz={tz} controls={ctl}
          onClose={() => { setOpenCall(null); void calls.refetch(); }} />
      )}
      <StatusBar shift={shift} phone={phone} />
      <div className="arc-ws-grid">
        <div className="arc-ws-main">
          {/* Who is on the line: this call's notes and tags, and the calls before it. Stays after the
              call as "Last call" until the next one or until closed. */}
          {panelNumber && (
            <CallerPanel tenantId={tenantId} extensionId={extensionId} number={panelNumber} live={!!phone.call}
              name={nameOf(panelNumber) ?? phone.call?.name ?? null} ctl={ctl} endedAt={endedAt}
              onClose={phone.call ? undefined : () => setPanelNumber(null)} />
          )}
          <CampaignPanel tenantId={tenantId} extensionId={extensionId} phone={phone} onActive={onCampaignActive}
            onDone={() => { if (shift.state === 'wrap_up') void shift.setState('ready'); }} />
        </div>
        {/* Beside the call on a desktop; under it, before the lists, on a phone. Greyed until the
            person is logged in with the phone up. */}
        <aside className="arc-ws-side" data-off={(!live && !phone.call) || undefined}>
          {/* A call takes over the dial pad's place: who it is, and its controls (owner, 3 October 2026). */}
          {phone.call ? (
            <section className="arc-card arc-ws-stage" data-live="" data-ringing={phone.call.state === 'ringing' || undefined}>
              <LiveCall phone={phone} contact={contacts[phone.call.number]} nameOf={nameOf} colleagues={colleagues} book={book} ctl={ctl} />
            </section>
          ) : shift.state === 'wrap_up' && !campaignActive ? (
            /* And wrap-up takes it after the call: what the call was, with the time left, and no
               dial pad, so the next call is not started over an unmarked one (owner, 3 October 2026). */
            <WrapStage shift={shift}>
              {ctl.outcome && (
                <WrapUp tenantId={tenantId} extensionId={extensionId} endedAt={endedAt || 1} embedded
                  onMarked={() => { if (shift.state === 'wrap_up') void shift.setState('ready'); }} />
              )}
            </WrapStage>
          ) : (
            <SidePanel phone={phone} canDial={canDial} colleagues={colleagues} book={book} />
          )}
        </aside>
        {/* Calls and the day as two tabs of one card (owner, 3 October 2026): a long list of calls
            under the stage pushed everything else off the screen. */}
        <section className="arc-card arc-ws-list">
          <Tabs style={{ padding: '0 14px' }}>
            <Tab active={lower === 'calls'} onSelect={() => setLower('calls')}>Recent calls</Tab>
            <Tab active={lower === 'day'} onSelect={() => setLower('day')}>Your day</Tab>
          </Tabs>
          {lower === 'calls'
            ? <RecentCalls rows={rows.slice(0, 10)} contacts={contacts} loading={calls.data === undefined}
                onDial={(n) => phone.dial(n)} canDial={canDial} onOpen={setOpenCall} />
            : <YourDay tenantId={tenantId} extensionId={extensionId} rows={today.data?.pages.flatMap((p) => p.data)}
                more={!!today.hasNextPage} endedAt={endedAt} />}
        </section>
      </div>
    </div>
  );
}

/**
 * Wrap-up in the dial pad's place: the time the queue gives, counting down, and what the call was.
 * Marking it ends wrap-up at once; when the time runs out the agent goes ready unmarked (StatusBar),
 * and the call can still be marked from Recent calls.
 */
function WrapStage({ shift, children }: { shift: ShiftApi; children: ReactNode }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const total = shift.wrapUp.seconds;
  const since = shift.stateSince ? new Date(shift.stateSince).getTime() : now;
  const left = Math.max(0, total * 1000 - (now - since));
  const secs = Math.ceil(left / 1000);
  return (
    <section className="arc-card arc-ws-stage arc-ws-wrapstage">
      <div className="arc-ws-wraphead">
        <strong>Wrapping up</strong>
        {total > 0 && <span className="arc-num" data-low={secs <= 5 || undefined}>{secs}s</span>}
      </div>
      {total > 0 && (
        <div className="arc-ws-wrapbar" role="progressbar" aria-label="Wrap-up time left"
          aria-valuemin={0} aria-valuemax={total} aria-valuenow={secs}>
          <span style={{ width: `${(left / (total * 1000)) * 100}%` }} />
        </div>
      )}
      {children}
      <div className="arc-ws-wrapfoot">
        <span className="arc-hint">
          {total > 0
            ? shift.requireWrapUp
              ? 'Mark the call to go ready. When the time is up you go ready anyway, and it can be marked later.'
              : 'You go ready when the time is up.'
            : shift.requireWrapUp ? 'Mark the call to go ready.' : 'Go ready when you are done.'}
        </span>
        {!shift.requireWrapUp && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={shift.busy}
            onClick={() => void shift.setState('ready')}>
            Ready now
          </button>
        )}
      </div>
    </section>
  );
}

// ================================================================== the status bar

/**
 * The bar along the top: the person's state and for how long, their shift, the phone, and one
 * Log in / Log out. Logging in starts the shift and the phone together (owner, 3 October 2026:
 * "what's this turn phone on?"); logging out ends both.
 */
function StatusBar({ shift, phone }: { shift: ShiftApi; phone: BrowserPhoneApi }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!shift.startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [shift.startedAt]);

  /**
   * Wrap-up ending itself: the seconds the queue asked for have passed, so the agent goes ready
   * without pressing anything -- even with the call unmarked where a mark is required (owner,
   * 3 October 2026): held past the time they sit idle while calls wait. The outcome can be added
   * later from the call.
   */
  const wrapDone = shift.state === 'wrap_up'
    && shift.wrapUp.seconds > 0 && shift.stateSince !== null
    && now - new Date(shift.stateSince).getTime() >= shift.wrapUp.seconds * 1000;
  useEffect(() => {
    if (wrapDone && !shift.busy) void shift.setState('ready');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapDone]);

  /**
   * Logged in but the phone is not up -- a reload, a phone that dropped: the shift is the
   * person's, so it stays, and the phone comes back with it unless another tab holds it.
   */
  useEffect(() => {
    if (shift.shiftId && phone.state === 'off' && !phone.elsewhere && phone.info?.available && phone.info.entitled) {
      void phone.signIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shift.shiftId, phone.info?.available]);

  const [busy, setBusy] = useState(false);
  const logIn = async (): Promise<void> => {
    setBusy(true);
    try {
      if (!shift.shiftId) await shift.logOn();
      if (phone.state !== 'ready' && !phone.elsewhere) await phone.signIn();
    } finally { setBusy(false); }
  };
  const logOut = async (): Promise<void> => {
    setBusy(true);
    try { await phone.signOut(); await shift.logOff(); } finally { setBusy(false); }
  };

  if (!shift.loaded) {
    return <section className="arc-card arc-ws-bar"><div className="arc-skeleton" style={{ height: 40, flex: 1 }} /></section>;
  }

  const phoneUsable = !!phone.info && phone.info.entitled && phone.info.available;
  const phoneLine = !phoneUsable ? null
    : phone.elsewhere ? (
      <span className="arc-ws-phone-note">
        The phone is open in another tab.
        <button type="button" className="arc-btn arc-btn-sm" onClick={() => void phone.takeOver()}>Use it here</button>
      </span>
    )
      : !shift.shiftId ? null
        : phone.state === 'reconnecting' ? <span className="arc-ws-phone-note" data-tone="warn">Reconnecting the phone…</span>
          : phone.state === 'failed' ? (
            <span className="arc-ws-phone-note" data-tone="danger">
              The phone is not connected.
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => void phone.signIn()}>Try again</button>
            </span>
          )
            : phone.state === 'connecting' ? <span className="arc-ws-phone-note">Connecting the phone…</span>
              : null;

  if (!shift.shiftId) {
    return (
      <section className="arc-card arc-ws-bar" data-state="off">
        <div className="arc-ws-state">
          <span className="arc-ws-dot" data-tone="off" aria-hidden="true" />
          <div className="arc-stack" style={{ gap: 1 }}>
            <strong className="arc-ws-state-label">{shift.endedElsewhere ? 'Your shift was ended' : 'Logged out'}</strong>
            <span className="arc-subtle" style={{ fontSize: 12 }}>
              {shift.endedElsewhere
                ? 'It ended somewhere else: a supervisor, another screen, or this one going quiet. Log in to carry on.'
                : 'Log in to start your shift and your phone. Your time is counted from here.'}
            </span>
          </div>
        </div>
        {phoneLine}
        <SettingsButton phone={phone} />
        <button type="button" className="arc-btn arc-btn-primary" disabled={busy || shift.busy || !phoneUsable && !!phone.info}
          onClick={() => void logIn()}>
          <Icon name="headset" size={14} /> Log in
        </button>
        {shift.error && <p className="arc-error arc-ws-bar-note">{shift.error}</p>}
        {phone.error && <p className="arc-error arc-ws-bar-note">{phone.error}</p>}
      </section>
    );
  }

  const onShift = Math.max(0, Math.floor((now - new Date(shift.startedAt!).getTime()) / 1000));
  const inState = shift.stateSince ? Math.max(0, Math.floor((now - new Date(shift.stateSince).getTime()) / 1000)) : 0;
  // Wrap-up counts DOWN: what somebody in it wants to know is how long they have (0111).
  const wrapLeft = Math.max(0, shift.wrapUp.seconds - inState);
  const onCall = shift.state === 'on_call';
  const paused = shift.state === 'not_ready';
  const wrapping = shift.state === 'wrap_up';

  return (
    <section className="arc-card arc-ws-bar" data-state={shift.state}>
      <div className="arc-ws-state">
        <span className="arc-ws-dot" data-tone={onCall ? 'info' : wrapping || paused ? 'warn' : 'ok'} aria-hidden="true" />
        <StateSelect shift={shift} />
        <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
          {wrapping && shift.wrapUp.seconds > 0 ? (wrapLeft > 0 ? `${wrapLeft}s left` : 'time is up') : spell(inState)}
        </span>
      </div>
      {phoneLine}
      <div className="arc-ws-shift">
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>On shift</span>
        <strong className="arc-num">{spell(onShift)}</strong>
      </div>
      <SettingsButton phone={phone} />
      <button type="button" className="arc-btn arc-btn-sm" disabled={busy || shift.busy || !!phone.call}
        title={phone.call ? 'Finish the call first' : undefined} onClick={() => void logOut()}>
        Log out
      </button>

      {onCall && (
        <p className="arc-hint arc-ws-bar-note">
          Your state follows the call, and goes back to {shift.reason ? 'your pause' : 'Ready'} when it ends.
        </p>
      )}
      {/* Said plainly: Ready refusing without a word is the kind of thing people work around
          rather than ask about. A pause is still open to them. */}
      {wrapping && (
        <p className="arc-hint arc-ws-bar-note">
          {shift.requireWrapUp
            ? 'Mark the call to go ready. You can still take a break from here.'
            : `Wrapping up${shift.wrapUp.seconds > 0 ? `; you go ready in ${wrapLeft}s` : ''}.`}
        </p>
      )}
      {shift.shiftId && shift.queues.length > 0 && <QueuePauses shift={shift} />}
      {shift.error && <p className="arc-error arc-ws-bar-note">{shift.error}</p>}
      {phone.error && <p className="arc-error arc-ws-bar-note">{phone.error}</p>}
    </section>
  );
}

/**
 * Taking calls in each queue, or paused in it with a reason (0156; owner, 4 October 2026: "pause
 * in specific queues while staying ready in the others"). The state above still pauses
 * everywhere at once; these are for one queue while the rest carry on.
 */
function QueuePauses({ shift }: { shift: ShiftApi }) {
  return (
    <div className="arc-ws-queues">
      <span className="arc-subtle" style={{ fontSize: 11.5 }}>Queues</span>
      {shift.queues.map((q) => (
        <label key={q.id} className="arc-ws-queue" data-paused={q.paused || undefined}>
          <span className="arc-ws-dot arc-ws-dot-sm" data-tone={q.paused ? 'warn' : 'ok'} aria-hidden="true" />
          <span className="arc-truncate">{q.name}</span>
          <select className="arc-select" aria-label={`${q.name}: taking calls or paused`}
            value={q.paused ? `p:${q.reason ?? ''}` : 'on'} disabled={shift.busy}
            onChange={(e) => {
              const v = e.target.value;
              void shift.setQueuePause(q.id, v !== 'on', v === 'on' ? null : v.slice(2));
            }}>
            <option value="on">Taking calls</option>
            <optgroup label="Pause in this queue">
              {shift.reasons.map((r) => <option key={r.code} value={`p:${r.code}`}>{r.label}</option>)}
            </optgroup>
          </select>
        </label>
      ))}
    </div>
  );
}

/**
 * The person's state as one choice (owner, 3 October 2026: "a drop down for pausing with reasons
 * ... linked to the pause stuff on the platform"): Ready, or a pause with one of the account's own
 * reasons (Agent codes), saying whether it counts as work. It follows the call while on one.
 */
function StateSelect({ shift }: { shift: ShiftApi }) {
  const onCall = shift.state === 'on_call';
  const wrapping = shift.state === 'wrap_up';
  const value = onCall ? '__call' : wrapping ? '__wrap' : shift.state === 'not_ready' ? `p:${shift.reason ?? ''}` : 'ready';
  return (
    <select className="arc-select arc-ws-state-select" aria-label="Your state" value={value}
      disabled={shift.busy || onCall}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'ready') void shift.setState('ready');
        else if (v.startsWith('p:')) void shift.setState('not_ready', v.slice(2) || null);
      }}>
      {onCall && <option value="__call">On a call</option>}
      {wrapping && <option value="__wrap">Wrapping up</option>}
      <option value="ready" disabled={wrapping && shift.requireWrapUp}>Ready</option>
      <optgroup label="Pause">
        {shift.reasons.map((r) => (
          <option key={r.code} value={`p:${r.code}`}>{r.label}{r.productive ? ' (counts as work)' : ''}</option>
        ))}
        {shift.reasons.length === 0 && <option value="p:">Not ready</option>}
      </optgroup>
    </select>
  );
}

/**
 * A duration in words rather than as a clock: 12:04 beside a shift reads as a time of day, and
 * "12m" cannot be misread. Seconds only matter under a minute, when something has just changed.
 */
function spell(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

// ================================================================== the stage

/**
 * The call in progress.
 *
 * Built around who is on it, with the controls as a row of labelled circles beneath: big, because
 * they are pressed in a hurry while writing something else; labelled, because guessing wrong here
 * hangs up on a customer. Hang up is the only red thing and sits at the end of the row.
 *
 * With a consultation up, the held caller and the person consulted are both shown, with what can
 * be done with the two: hand the caller over, swap, join all three, or go back.
 */
function LiveCall({ phone, contact, nameOf, colleagues, book, ctl }: {
  phone: BrowserPhoneApi; contact: PhonebookMatch | undefined;
  nameOf: (n: string) => string | undefined; colleagues: Colleague[] | undefined; book: Book;
  ctl: WorkspaceControls;
}) {
  const call = phone.call!;
  const [panel, setPanel] = useState<'none' | 'keypad' | 'transfer'>('none');
  const [sent, setSent] = useState('');
  const seconds = useTimer(call.answeredAt);

  // What was just sent on the keypad, shown for a moment so a mis-hit is visible.
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(''), 2500);
    return () => clearTimeout(t);
  }, [sent]);
  useEffect(() => { if (phone.consult) setPanel('none'); }, [phone.consult]);

  const ringing = call.state === 'ringing';
  const held = call.state === 'held';
  const status = ringing ? (call.inbound ? 'Incoming' : 'Ringing')
    : call.state === 'calling' ? 'Calling' : formatDuration(seconds);
  const name = contact?.name ?? call.name ?? nameOf(call.number) ?? readable(call.number);

  return (
    <div className="arc-call" data-state={call.state}>
      <Party call={call} name={name} status={status} contact={contact} />
      {call.audio !== 'ok' && (
        <p className="arc-ws-audio-note" data-tone={call.audio === 'lost' ? 'danger' : 'warn'}>
          <Icon name="warning" size={13} />
          {call.audio === 'lost'
            ? 'The audio path is lost. This network may be blocking calls; the call is still up.'
            : 'The network changed. Restoring the audio…'}
        </p>
      )}

      {phone.consult && (
        <ConsultBar phone={phone} consult={phone.consult} ctl={ctl}
          name={nameOf(phone.consult.number) ?? phone.consult.name ?? readable(phone.consult.number)} />
      )}

      {ringing ? (
        <div className="arc-call-controls" data-answer="true">
          <RoundButton tone="answer" icon="phone" label="Answer" big onClick={phone.answer} />
          {ctl.hang_up && <RoundButton tone="end" icon="close" label="Refuse" big onClick={phone.hangUp} />}
        </div>
      ) : (
        <>
          {/* Only what this agent is given (0153): a control switched off is not there at all. */}
          <div className="arc-call-controls" style={{ gridTemplateColumns: `repeat(${[ctl.mute, ctl.hold, ctl.keypad,
            ctl.transfer || ctl.talk_first, ctl.hang_up].filter(Boolean).length || 1}, minmax(0, 1fr))` }}>
            {ctl.mute && <RoundButton icon="mute" label={call.muted ? 'Unmute' : 'Mute'} on={call.muted}
              onClick={() => phone.mute(!call.muted)} />}
            {ctl.hold && <RoundButton icon="hold" label={held ? 'Resume' : 'Hold'} on={held} disabled={!!phone.consult}
              onClick={() => phone.hold(!held)} />}
            {ctl.keypad && <RoundButton icon="hash" label="Keypad" on={panel === 'keypad'}
              onClick={() => setPanel((x) => (x === 'keypad' ? 'none' : 'keypad'))} />}
            {(ctl.transfer || ctl.talk_first) && <RoundButton icon="transfer" label="Transfer" on={panel === 'transfer'} disabled={!!phone.consult}
              onClick={() => setPanel((x) => (x === 'transfer' ? 'none' : 'transfer'))} />}
            {ctl.hang_up && <RoundButton tone="end" icon="phone" label="Hang up" onClick={phone.hangUp} />}
          </div>
          <CallWaves phone={phone} />

          {panel === 'keypad' && (
            <div className="arc-call-panel">
              <div className="arc-call-sent arc-mono" aria-live="polite">{sent || ' '}</div>
              <Keypad onPress={(d) => { phone.sendDtmf(d); setSent((v) => v + d); }} />
              <div className="arc-hint">Tones go to whoever you are talking to. This does not start a new call.</div>
            </div>
          )}
          {panel === 'transfer' && (
            <TransferPanel colleagues={colleagues} book={book} ctl={ctl}
              onNow={(to) => { phone.transfer(to); setPanel('none'); }}
              onTalkFirst={(to) => { phone.consultStart(to); setPanel('none'); }} />
          )}
        </>
      )}
    </div>
  );
}

interface Earlier {
  id: string; started_at: string; direction: string; answered: boolean; duration_sec: number | null;
  agent: string | null; agent_number: string | null;
  outcome: { label: string; kind: 'positive' | 'neutral' | 'negative' } | null;
  tags: string[];
  notes: { count: number; latest: string; author: string | null } | null;
}

/**
 * The caller on the line (owner, 3 October 2026: "use that empty card space ... for notes and tags
 * ... populate previous calls or interactions ... and who dealt with them"). Left, what is being
 * noted on this call; right, the account's earlier calls with this number.
 */
function CallerPanel({ tenantId, extensionId, number, name, live, ctl, endedAt, onClose }: {
  tenantId: string; extensionId: string; number: string; name: string | null; live: boolean;
  ctl: WorkspaceControls; endedAt: number; onClose: (() => void) | undefined;
}) {
  const external = number.replace(/\D/g, '').length > 6;
  const history = useQuery({
    queryKey: ['caller-history', tenantId, extensionId, number, endedAt],
    queryFn: () => api.get<{ data: Earlier[] }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/caller-history?number=${encodeURIComponent(number)}`),
    enabled: external,
    retry: false,
  });
  const notes = ctl.notes || ctl.tags;
  return (
    <section className="arc-card arc-ws-caller">
      <div className="arc-ws-caller-head">
        <span className="arc-ws-dot arc-ws-dot-sm" data-tone={live ? 'ok' : 'off'} aria-hidden="true" />
        <span className="arc-subtle" style={{ fontSize: 12 }}>{live ? 'On the line' : 'Last call'}</span>
        <strong className="arc-truncate">{name ?? readable(number)}</strong>
        {name && <span className="arc-subtle arc-mono" style={{ fontSize: 12 }}>{readable(number)}</span>}
        <span className="arc-spacer" />
        {onClose && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Close" title="Close" onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
      <div className="arc-ws-caller-body" data-one={!notes || undefined}>
        {notes && (
          <div className="arc-ws-caller-col">
            <span className="arc-cw-label">This call</span>
            {live ? <InCallNotes ctl={ctl} /> : <SavedNotes />}
          </div>
        )}
        <div className="arc-ws-caller-col">
          <span className="arc-cw-label">Earlier calls</span>
          {!external ? <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>A colleague: earlier calls are kept for outside numbers.</p>
            : history.isLoading ? <div className="arc-skeleton" style={{ height: 80 }} />
              : history.error ? <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>Earlier calls could not be read.</p>
                : (history.data?.data.length ?? 0) <= (live ? 0 : 1) ? <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>No earlier calls with this number.</p>
                  : (
                    <ul className="arc-ws-earlier">
                      {history.data!.data.map((e) => (
                        <li key={e.id}>
                          <div className="arc-ws-earlier-top">
                            <Icon name={e.direction === 'outbound' ? 'callOut' : 'callIn'} size={12} />
                            <span>{relativeTime(e.started_at)}</span>
                            <span className="arc-subtle">· {e.agent ?? 'Nobody'}</span>
                            <span className="arc-subtle">· {e.answered ? formatDuration(e.duration_sec ?? 0) : 'missed'}</span>
                            <span className="arc-spacer" />
                            <Outcome d={e.outcome ? { code: '', label: e.outcome.label, kind: e.outcome.kind, note: null } : null} />
                          </div>
                          {e.tags.length > 0 && <TagList tags={e.tags} />}
                          {e.notes && (
                            <p className="arc-ws-earlier-note">
                              {e.notes.latest}
                              <span className="arc-subtle"> — {e.notes.author ?? 'somebody'}{e.notes.count > 1 ? `, ${e.notes.count} notes` : ''}</span>
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
        </div>
      </div>
    </section>
  );
}

/** After the call: whether what was noted during it has reached the call's record. */
function SavedNotes() {
  const { inCall } = usePhone();
  const s = inCall?.saved ?? 'idle';
  return (
    <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>
      {s === 'waiting' ? 'Saving your notes and tags to the call…'
        : s === 'saved' ? 'Your notes and tags are saved to the call. Open it from Recent calls to add more.'
          : s === 'failed' ? 'Your notes could not be saved to the call. Open it from Recent calls to add them.'
            : 'Nothing was noted during the call. Open it from Recent calls to add notes.'}
    </p>
  );
}

/**
 * Notes and tags while the call is on (owner, 3 October 2026): kept by the phone and attached to
 * the call once its record arrives, a few seconds after it ends.
 */
function InCallNotes({ ctl }: { ctl: WorkspaceControls }) {
  const { inCall } = usePhone();
  const [draft, setDraft] = useState('');
  if (!inCall) return null;
  const add = (): void => { inCall.addNote(draft); setDraft(''); };
  return (
    <div className="arc-ws-incall" style={{ borderTop: 0, paddingTop: 0 }}>
      {ctl.tags && inCall.options.length > 0 && (
        <div className="arc-chip-list">
          {inCall.options.map((t) => (
            <button key={t} type="button" className="arc-chip arc-chip-toggle" aria-pressed={inCall.tags.includes(t)}
              onClick={() => inCall.toggleTag(t)}>{t}</button>
          ))}
        </div>
      )}
      {ctl.notes && (
        // Room to write while listening (owner, 3 October 2026): Enter is a new line; Ctrl+Enter
        // (Cmd+Enter on a Mac) or the button adds the note.
        <div className="arc-stack" style={{ gap: 6 }}>
          <textarea className="arc-input arc-ws-notebox" placeholder="A note on this call" value={draft} maxLength={4000}
            rows={5} aria-label="A note on this call" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); add(); } }} />
          <div className="arc-row" style={{ gap: 8, justifyContent: 'space-between' }}>
            <span className="arc-hint" style={{ margin: 0 }}>Ctrl+Enter adds it.</span>
            <button type="button" className="arc-btn arc-btn-sm arc-btn-primary" disabled={!draft.trim()} onClick={add}>Add note</button>
          </div>
        </div>
      )}
      {inCall.notes.length > 0 && (
        <ul className="arc-ws-incall-notes">{inCall.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
      )}
      <span className="arc-hint" style={{ margin: 0 }}>Saved to the call when it ends.</span>
    </div>
  );
}

/** A call's clock, from the moment it was answered. */
function useTimer(answeredAt: number | null): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (answeredAt === null) { setSeconds(0); return; }
    const tick = (): void => setSeconds(Math.floor((Date.now() - answeredAt) / 1000));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [answeredAt]);
  return seconds;
}

/** Who is on a call: the avatar, the direction and clock, the name, and what is being done to it. */
function Party({ call, name, status, contact }: {
  call: PhoneCall; name: string; status: string; contact?: PhonebookMatch | undefined;
}) {
  const ringing = call.state === 'ringing';
  return (
    <div className="arc-call-who">
      <span className="arc-call-avatar" data-ringing={ringing || undefined}
        data-wide={initials(name).length > 2 || undefined} aria-hidden="true">
        {initials(name)}
      </span>
      <div className="arc-call-top">
        <Icon name={call.inbound ? 'callIn' : 'callOut'} size={13} />
        <span className="arc-call-kind">{call.inbound ? 'Inbound' : 'Outbound'}</span>
        <span className="arc-call-gap" />
        <span className="arc-call-timer arc-num" aria-live="polite">{status}</span>
      </div>
      <strong className="arc-call-name arc-truncate">{name}</strong>
      <div className="arc-call-sub">
        {name !== readable(call.number) && <span className="arc-call-number arc-mono arc-truncate">{readable(call.number)}</span>}
        {/* Where the name came from: the account's own phonebook, not the network. */}
        {contact && (
          <span className="arc-call-from-book" title={`${contact.name}${contact.company ? ` · ${contact.company}` : ''} — from the phonebook`}>
            <Icon name="contacts" size={11} />
            {contact.company ?? 'Phonebook'}
          </span>
        )}
      </div>
    </div>
  );
}

/** Talking first: the person consulted, and the four things that can happen next. */
function ConsultBar({ phone, consult, name, ctl }: { phone: BrowserPhoneApi; consult: PhoneCall; name: string; ctl: WorkspaceControls }) {
  const seconds = useTimer(consult.answeredAt);
  const up = consult.state === 'answered' || consult.state === 'held';
  const status = consult.state === 'calling' || consult.state === 'ringing' ? 'Ringing' : formatDuration(seconds);
  return (
    <div className="arc-ws-consult">
      <span className="arc-subtle" style={{ fontSize: 11.5 }}>
        {phone.conference ? 'All three on the call' : 'Talking first, before handing over'}
      </span>
      <Party call={consult} name={name} status={status} />
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {!phone.conference && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!up} onClick={phone.consultComplete}>
            <Icon name="transfer" size={13} /> Hand over the caller
          </button>
        )}
        {!phone.conference && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={!up} onClick={phone.consultSwap}>Swap</button>
        )}
        {!phone.conference && ctl.conference && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={!up} onClick={phone.merge}>
            <Icon name="users" size={13} /> Join all three
          </button>
        )}
        <button type="button" className="arc-btn arc-btn-sm" onClick={phone.consultCancel}>
          {phone.conference ? `Drop ${name.split(' ')[0]}` : 'Back to the caller'}
        </button>
      </div>
    </div>
  );
}

/**
 * Where a call goes: found by name, extension or number, with colleagues' states beside them so
 * nobody transfers a caller to somebody already on a call. Now, or after talking to them first.
 */
function TransferPanel({ colleagues, book, onNow, onTalkFirst, ctl }: {
  colleagues: Colleague[] | undefined; book: Book;
  onNow: (to: string) => void; onTalkFirst: (to: string) => void; ctl: WorkspaceControls;
}) {
  const [to, setTo] = useState('');
  const [picked, setPicked] = useState<Hit | null>(null);
  const target = picked?.number ?? to.trim();
  return (
    <div className="arc-call-panel">
      <SearchBox value={to} onChange={(v) => { setTo(v); setPicked(null); }} colleagues={colleagues} book={book}
        onPick={(h) => { setPicked(h); setTo(h.name); }} picked={picked} autoFocus placeholder="Name, extension or number" />
      <div className="arc-row" style={{ gap: 8 }}>
        {ctl.talk_first && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!target} onClick={() => onTalkFirst(target)}>
            Talk first
          </button>
        )}
        {ctl.transfer && (
          <button type="button" className={`arc-btn arc-btn-sm${ctl.talk_first ? '' : ' arc-btn-primary'}`} disabled={!target} onClick={() => onNow(target)}>
            Transfer now
          </button>
        )}
      </div>
      <div className="arc-hint">
        Talk first puts the caller on hold and rings them, so you can introduce the call before handing it over.
        Transfer now hands it straight over, whether or not they answer.
      </div>
    </div>
  );
}

/** One control: a circle to hit, and a word underneath saying what it does. */
function RoundButton({ icon, label, onClick, on, tone, big, disabled }: {
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  onClick: () => void;
  on?: boolean;
  tone?: 'answer' | 'end';
  big?: boolean;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="arc-call-btn" data-tone={tone} data-big={big || undefined}
      data-on={on || undefined} aria-pressed={tone ? undefined : !!on} disabled={disabled}
      data-rot={tone === 'end' && icon === 'phone' ? '' : undefined}
      aria-label={label} onClick={onClick}>
      <span className="arc-call-circle"><Icon name={icon} size={big ? 21 : 17} /></span>
      <span className="arc-call-label">{label}</span>
    </button>
  );
}

/** Two letters for the avatar: a name's initials, or the last two digits of a number. */
function initials(who: string): string {
  const w = who.trim();
  if (!w) return '?';
  if (/^[+0-9]/.test(w)) {
    const digits = w.replace(/\D/g, '');
    // An extension is short enough to show whole; a public number is not, so it gives its last two.
    return (digits.length <= 4 ? digits : digits.slice(-2)) || '?';
  }
  const words = w.split(/\s+/);
  return (words[0]![0]! + (words[1]?.[0] ?? '')).toUpperCase();
}

/**
 * A number as somebody would say it: 081 266 8181, +27 81 266 8181. An extension is left alone --
 * it is four digits and grouping it would make it look like something else.
 */
export function readable(number: string): string {
  const n = number.trim();
  if (!/^\+?[0-9]+$/.test(n) || n.length <= 6) return n;
  if (n.startsWith('+27') && n.length === 12) return `+27 ${n.slice(3, 5)} ${n.slice(5, 8)} ${n.slice(8)}`;
  if (n.startsWith('0') && n.length === 10) return `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
  return n;
}

/**
 * The keypad, with the letters under the digits: menus are read out as "press 2 for accounts", and
 * the dial search finds names by them. Three fixed columns at every width.
 */
const KEYS: ReadonlyArray<readonly [string, string]> = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
];

function Keypad({ onPress, disabled }: { onPress: (d: string) => void; disabled?: boolean }) {
  return (
    <div className="arc-keypad">
      {KEYS.map(([digit, letters]) => (
        <button key={digit} type="button" className="arc-key" disabled={disabled} onClick={() => onPress(digit)}>
          <span className="arc-key-digit">{digit}</span>
          {letters && <span className="arc-key-letters">{letters}</span>}
        </button>
      ))}
    </div>
  );
}

/** A colleague's state as a dot, named for anybody who cannot see the colour. */
function StateDot({ state }: { state: Colleague['state'] }) {
  return <span className="arc-ws-dot arc-ws-dot-sm" data-tone={STATE_TONE[state]} title={STATE_LABEL[state]} role="img" aria-label={STATE_LABEL[state]} />;
}

/**
 * A number box that finds as it is typed: digits match extensions, numbers and -- by the keypad's
 * letters -- names; letters match names and companies. The results sit under it, colleagues with
 * their state.
 */
function SearchBox({ value, onChange, onPick, picked, colleagues, book, autoFocus, placeholder, onEnter, disabled }: {
  value: string; onChange: (v: string) => void; onPick: (h: Hit) => void; picked?: Hit | null;
  colleagues: Colleague[] | undefined; book: Book;
  autoFocus?: boolean; placeholder: string; onEnter?: () => void; disabled?: boolean;
}) {
  const hits = useSearch(picked ? '' : value, colleagues, book);
  return (
    <div className="arc-ws-search">
      <div className="arc-ws-number">
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- opened to type into */}
        <input className="arc-input" autoFocus={autoFocus} placeholder={placeholder} aria-label={placeholder}
          value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (!picked && hits[0] && !/^\+?[0-9]+$/.test(value.trim())) onPick(hits[0]); else onEnter?.();
          }} />
        {value && !disabled && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Delete the last character"
            onClick={() => onChange(value.slice(0, -1))}>
            <Icon name="arrowLeft" size={14} />
          </button>
        )}
      </div>
      {/* The chosen one's number and state sit inside the box: nothing below it moves. */}
      {picked && (
        <span className="arc-ws-picked">
          {picked.state && <StateDot state={picked.state} />}
          <span className="arc-mono">{readable(picked.number)}</span>
        </span>
      )}
      {/* Over the keypad, not above it: the keys stay where the hand expects them (owner, 3 October 2026). */}
      {hits.length > 0 && (
        <ul className="arc-ws-hits" aria-label="Matches">
          {hits.map((h) => (
            <li key={h.key}>
              <button type="button" onClick={() => onPick(h)}>
                {h.kind === 'extension' && h.state ? <StateDot state={h.state} /> : <Icon name="contacts" size={13} />}
                <span className="arc-stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
                  <span className="arc-truncate" style={{ fontWeight: 550 }}>{h.name}</span>
                  {h.sub && <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>{h.sub}</span>}
                </span>
                <span className="arc-mono arc-subtle" style={{ fontSize: 12 }}>{readable(h.number)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ================================================================== the side column

/** The dial pad and the colleagues, as two tabs of one card. */
function SidePanel({ phone, canDial, colleagues, book }: {
  phone: BrowserPhoneApi; canDial: boolean; colleagues: Colleague[] | undefined; book: Book;
}) {
  const [tab, setTab] = useState<'dial' | 'people' | 'messages'>('dial');
  const unread = useMessages((s) => Object.values(s.threads).reduce((n, t) => n + t.unread, 0));
  const setOpen = useMessages((s) => s.setOpen);
  // Leaving the tab closes the conversation, so what arrives meanwhile counts as unread.
  useEffect(() => { if (tab !== 'messages') setOpen(null); }, [tab, setOpen]);
  return (
    <section className="arc-card">
      <Tabs style={{ padding: '0 14px' }}>
        <Tab active={tab === 'dial'} onSelect={() => setTab('dial')}>Dial</Tab>
        <Tab active={tab === 'people'} onSelect={() => setTab('people')}>Colleagues</Tab>
        <Tab active={tab === 'messages'} onSelect={() => setTab('messages')}>
          Messages{unread > 0 && <span className="arc-ws-msg-count" aria-label={`${unread} unread`}>{unread}</span>}
        </Tab>
      </Tabs>
      {tab === 'dial'
        ? <Dialler phone={phone} canDial={canDial} colleagues={colleagues} book={book} />
        : tab === 'people'
          ? <Colleagues colleagues={colleagues} canDial={canDial} onDial={(n) => phone.dial(n)}
              onMessage={(n) => { setOpen(n); setTab('messages'); }} />
          : <Messages phone={phone} colleagues={colleagues} />}
    </section>
  );
}

// ================================================================== messages

/**
 * Messages with colleagues (owner, 3 October 2026: "workspace should have the messaging
 * function"): the conversations, newest first, and one conversation at a time. They reach every
 * device a colleague has that shows messages -- browser, desktop or mobile app -- and a message to
 * somebody with none says so, rather than pretending it arrived.
 */
function Messages({ phone, colleagues }: { phone: BrowserPhoneApi; colleagues: Colleague[] | undefined }) {
  const threads = useMessages((s) => s.threads);
  const open = useMessages((s) => s.open);
  const setOpen = useMessages((s) => s.setOpen);
  const [q, setQ] = useState('');
  const who = (ext: string): Colleague | undefined => colleagues?.find((c) => c.number === ext);
  if (open) return <Conversation ext={open} who={who(open)} phone={phone} onBack={() => setOpen(null)} />;

  const list = Object.values(threads).filter((t) => t.messages.length > 0)
    .sort((a, b) => (b.messages.at(-1)?.at ?? 0) - (a.messages.at(-1)?.at ?? 0));
  const t = q.trim().toLowerCase();
  const start = t ? (colleagues ?? []).filter((c) => c.name.toLowerCase().includes(t) || c.number.includes(t)).slice(0, 6) : [];
  return (
    <div>
      <div className="arc-card-body" style={{ paddingBottom: 8 }}>
        <input className="arc-input" placeholder="Message a colleague" aria-label="Message a colleague"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {start.length > 0 ? (
        <ul className="arc-ws-people-list">
          {start.map((c) => (
            <li key={c.id}>
              <StateDot state={c.state} />
              <button type="button" className="arc-ws-msg-pick" onClick={() => { setQ(''); setOpen(c.number); }}>
                <span className="arc-truncate" style={{ fontWeight: 550 }}>{c.name}</span>
                <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>
                  {c.number}{c.messaging ? '' : ' · can\'t receive messages'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : list.length === 0 ? (
        <p className="arc-subtle" style={{ padding: '0 16px 14px', margin: 0, fontSize: 12.5 }}>
          {t ? 'Nobody matches.' : 'No messages yet. Find a colleague above, or use the message button in Colleagues.'}
        </p>
      ) : (
        <ul className="arc-ws-msg-threads">
          {list.map((th) => {
            const last = th.messages.at(-1)!;
            const c = who(th.ext);
            return (
              <li key={th.ext}>
                <button type="button" onClick={() => setOpen(th.ext)} data-unread={th.unread > 0 || undefined}>
                  <span className="arc-ws-msg-thread-top">
                    <strong className="arc-truncate">{c?.name ?? th.ext}</strong>
                    <span className="arc-subtle arc-num">{relativeTime(new Date(last.at).toISOString())}</span>
                  </span>
                  <span className="arc-ws-msg-thread-last arc-truncate">
                    {last.dir === 'out' ? 'You: ' : ''}{last.text}
                  </span>
                  {th.unread > 0 && <span className="arc-ws-msg-count">{th.unread}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Conversation({ ext, who, phone, onBack }: {
  ext: string; who: Colleague | undefined; phone: BrowserPhoneApi; onBack: () => void;
}) {
  const thread = useMessages((s) => s.threads[ext]);
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const count = thread?.messages.length ?? 0;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [count]);
  const ready = phone.state === 'ready';
  const send = (): void => {
    const body = text.trim();
    if (!body || !ready) return;
    phone.sendMessage(ext, body);
    setText('');
  };
  return (
    <div className="arc-ws-msg-conv">
      <div className="arc-ws-msg-head">
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="All messages" title="All messages" onClick={onBack}>
          <Icon name="chevronLeft" size={15} />
        </button>
        {who && <StateDot state={who.state} />}
        <span className="arc-stack" style={{ gap: 0, minWidth: 0 }}>
          <strong className="arc-truncate">{who?.name ?? ext}</strong>
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
            {ext}{who ? ` · ${STATE_LABEL[who.state].toLowerCase()}` : ''}{who && !who.messaging ? ' · can\'t receive messages' : ''}
          </span>
        </span>
      </div>
      <div className="arc-ws-msg-body" role="log" aria-live="polite">
        {count === 0 && <p className="arc-subtle" style={{ margin: 'auto', fontSize: 12.5 }}>No messages with {who?.name ?? ext} yet.</p>}
        {thread?.messages.map((m) => (
          <div key={m.id} className="arc-ws-msg" data-dir={m.dir}>
            <div className="arc-ws-msg-bubble">{m.text}</div>
            <div className="arc-ws-msg-meta arc-subtle">
              {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {m.status === 'sending' ? ' · sending' : m.status === 'unavailable' ? ' · not delivered: they can\'t receive messages'
                : m.status === 'failed' ? ' · not delivered' : ''}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form className="arc-ws-msg-compose" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea className="arc-input" rows={2} aria-label={`Message ${who?.name ?? ext}`} disabled={!ready}
          placeholder={ready ? 'Write a message' : 'Log in to send messages'} value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <button type="submit" className="arc-btn arc-btn-primary arc-btn-icon" aria-label="Send" title="Send (Enter)" disabled={!ready || !text.trim()}>
          <Icon name="send" size={15} />
        </button>
      </form>
    </div>
  );
}

/** Dialling: type a name or a number, or use the keys; the call button under them. */
function Dialler({ phone, canDial, colleagues, book }: {
  phone: BrowserPhoneApi; canDial: boolean; colleagues: Colleague[] | undefined; book: Book;
}) {
  const [number, setNumber] = useState('');
  const [picked, setPicked] = useState<Hit | null>(null);
  const target = picked?.number ?? number.trim();
  const place = (): void => { if (canDial && target) { phone.dial(target); setNumber(''); setPicked(null); } };
  return (
    <div className="arc-card-body arc-stack" style={{ gap: 10 }}>
      <SearchBox value={number} onChange={(v) => { setNumber(v); setPicked(null); }} picked={picked}
        onPick={(h) => { setPicked(h); setNumber(h.name); }} colleagues={colleagues} book={book}
        placeholder="Name, extension or number" onEnter={place} disabled={!canDial} />
      <Keypad disabled={!canDial} onPress={(d) => { setPicked(null); setNumber((n) => n + d); }} />
      <button type="button" className="arc-btn arc-btn-primary arc-ws-call" disabled={!canDial || !target} onClick={place}>
        <Icon name="phone" size={15} /> Call{picked ? ` ${picked.name.split(' ')[0]}` : ''}
      </button>
      {!canDial && !phone.call && <p className="arc-hint" style={{ margin: 0 }}>Log in to make calls.</p>}
    </div>
  );
}

/** The company's extensions with what each is doing, live, for calling a colleague or checking first. */
function Colleagues({ colleagues, canDial, onDial, onMessage }: {
  colleagues: Colleague[] | undefined; canDial: boolean; onDial: (n: string) => void; onMessage: (n: string) => void;
}) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (colleagues ?? []).filter((c) => !t || c.name.toLowerCase().includes(t) || c.number.includes(t)
      || (c.department ?? '').toLowerCase().includes(t));
  }, [colleagues, q]);
  if (colleagues === undefined) return <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 120 }} /></div>;
  return (
    <div>
      <div className="arc-card-body" style={{ paddingBottom: 8 }}>
        <input className="arc-input" placeholder="Search colleagues" aria-label="Search colleagues" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {shown.length === 0 ? (
        <p className="arc-subtle" style={{ padding: '0 16px 14px', margin: 0, fontSize: 12.5 }}>
          {colleagues.length === 0 ? 'Nobody is in the company directory yet.' : 'Nobody matches.'}
        </p>
      ) : (
        <ul className="arc-ws-people-list">
          {shown.map((c) => (
            <li key={c.id}>
              <StateDot state={c.state} />
              <span className="arc-stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
                <span className="arc-truncate" style={{ fontWeight: 550 }}>{c.name}</span>
                <span className="arc-subtle arc-truncate" style={{ fontSize: 11.5 }}>
                  {c.number}{c.department ? ` · ${c.department}` : ''} · {STATE_LABEL[c.state].toLowerCase()}
                </span>
              </span>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label={`Message ${c.name}`}
                title={c.messaging ? `Message ${c.name}` : `Message ${c.name} (none of their devices shows messages now)`}
                onClick={() => onMessage(c.number)}>
                <Icon name="message" size={14} />
              </button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label={`Call ${c.name}`}
                title={`Call ${c.name}`} disabled={!canDial} onClick={() => onDial(c.number)}>
                <Icon name="phone" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The phone's settings, in a window of their own from a button in the status bar (owner, 3 October
 * 2026: "the audio stuff and settings should be in its own settings menu ... a modal").
 */
function SettingsButton({ phone }: { phone: BrowserPhoneApi }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" title="Phone settings"
        aria-label="Phone settings" onClick={() => setOpen(true)}>
        <Icon name="settings" size={16} />
      </button>
      {open && (
        <Modal title="Phone settings" size="sm" onClose={() => setOpen(false)}>
          <AudioSettings phone={phone} />
        </Modal>
      )}
    </>
  );
}

/**
 * Microphone and speaker, kept in this browser. The names appear once the browser has been allowed
 * the microphone; the test shows the level without making a call.
 */
function AudioSettings({ phone }: { phone: BrowserPhoneApi }) {
  const [testing, setTesting] = useState<MediaStream | null>(null);
  useEffect(() => () => testing?.getTracks().forEach((t) => t.stop()), [testing]);
  const d = phone.devices;
  const test = async (): Promise<void> => {
    if (testing) { setTesting(null); return; }
    try {
      setTesting(await navigator.mediaDevices.getUserMedia({ audio: phone.micId ? { deviceId: { exact: phone.micId } } : true }));
    } catch { /* refused: the choices below still work */ }
  };
  return (
    <div className="arc-stack" style={{ gap: 12, padding: '16px 18px 18px' }}>
        {!d.named && (
          <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="arc-hint" style={{ margin: 0 }}>The browser names your devices once it may use the microphone.</span>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => void phone.allowDevices()}>Allow</button>
          </div>
        )}
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ws-mic">Microphone</label>
          <select id="ws-mic" className="arc-select" value={phone.micId ?? ''} onChange={(e) => phone.setMic(e.target.value || null)}>
            <option value="">System default</option>
            {d.inputs.filter((x) => x.id !== 'default').map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="ws-spk">Speaker</label>
          <select id="ws-spk" className="arc-select" value={phone.speakerId ?? ''} disabled={!d.canChooseSpeaker}
            onChange={(e) => phone.setSpeaker(e.target.value || null)}>
            <option value="">System default</option>
            {d.outputs.filter((x) => x.id !== 'default').map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
          {!d.canChooseSpeaker && <p className="arc-hint">This browser plays calls through the system speaker. Chrome and Edge can choose.</p>}
        </div>
        <div className="arc-row" style={{ gap: 10 }}>
          <button type="button" className="arc-btn arc-btn-sm" disabled={!!phone.call} onClick={() => void test()}>
            {testing ? 'Stop the test' : 'Test the microphone'}
          </button>
          {testing && <Meter track={testing.getAudioTracks()[0] ?? null} />}
        </div>
    </div>
  );
}

/**
 * Both sides of the call as rolling waveforms (owner, 3 October 2026: "wave form both sides of the
 * call"): you, and whoever you are talking to, so a dead microphone or a silent line is seen, not
 * guessed. Always the same two rows, so nothing below them moves: muted or held is said beside the
 * wave, not by taking it away.
 */
function CallWaves({ phone }: { phone: BrowserPhoneApi }) {
  const [mine, setMine] = useState<MediaStreamTrack | null>(null);
  const [theirs, setTheirs] = useState<MediaStreamTrack | null>(null);
  const { micTrack, remoteTrack } = phone;
  useEffect(() => {
    const look = (): void => { setMine(micTrack()); setTheirs(remoteTrack()); };
    look();
    const t = setInterval(look, 1000);
    return () => clearInterval(t);
  }, [micTrack, remoteTrack]);
  const muted = !!phone.call?.muted;
  const held = phone.call?.state === 'held';
  return (
    <div className="arc-ws-waves">
      <div className="arc-ws-waverow">
        <span className="arc-ws-wavelabel">You</span>
        <div className="arc-ws-wavebox">
          <Wave track={muted || held ? null : mine} tone="you" />
          {(muted || held) && <span className="arc-ws-micstate" data-tone={muted ? 'warn' : 'info'}>{muted ? 'Muted' : 'On hold'}</span>}
        </div>
      </div>
      <div className="arc-ws-waverow">
        <span className="arc-ws-wavelabel">Caller</span>
        <div className="arc-ws-wavebox">
          <Wave track={held ? null : theirs} tone="them" />
          {held && <span className="arc-ws-micstate" data-tone="info">On hold</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * A rolling waveform of one audio track: a bar per 40 ms, newest on the right, about four seconds
 * across. No track draws a flat line, which is what silence looks like.
 */
function Wave({ track, tone }: { track: MediaStreamTrack | null; tone: 'you' | 'them' }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const g = el.getContext('2d');
    if (!g) return;
    const dpr = window.devicePixelRatio || 1;
    const w = el.clientWidth, h = el.clientHeight;
    el.width = Math.round(w * dpr); el.height = Math.round(h * dpr);
    g.scale(dpr, dpr);
    const color = getComputedStyle(el).color;
    const step = 3; // bar 2 px, gap 1 px
    const levels: number[] = new Array(Math.floor(w / step)).fill(0);
    const paint = (): void => {
      g.clearRect(0, 0, w, h);
      g.fillStyle = color;
      levels.forEach((v, i) => {
        const bh = Math.max(1.5, v * (h - 2));
        g.fillRect(i * step, (h - bh) / 2, 2, bh);
      });
    };
    let ctx: AudioContext | null = null;
    let an: AnalyserNode | null = null;
    if (track && track.readyState === 'live') {
      ctx = new AudioContext();
      an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(new MediaStream([track])).connect(an);
    }
    const buf = new Uint8Array(512);
    const tick = setInterval(() => {
      let peak = 0;
      if (an) {
        an.getByteTimeDomainData(buf);
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      }
      levels.shift();
      levels.push(Math.min(1, (peak / 128) * 1.8));
      paint();
    }, 40);
    paint();
    return () => { clearInterval(tick); void ctx?.close(); };
  }, [track]);
  return <canvas ref={canvas} className="arc-ws-wave" data-tone={tone} aria-hidden="true" />;
}

/** A level bar for one audio track. */
function Meter({ track }: { track: MediaStreamTrack | null }) {
  const bar = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!track) return;
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(new MediaStream([track]));
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    const buf = new Uint8Array(an.fftSize);
    let raf = 0;
    const draw = (): void => {
      an.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      if (bar.current) bar.current.style.width = `${Math.min(100, (peak / 128) * 160)}%`;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); void ctx.close(); };
  }, [track]);
  return <span className="arc-ws-meter" aria-hidden="true"><span ref={bar} /></span>;
}

// ================================================================== the day

/**
 * The person's day (owner, 3 October 2026: "a place to see stats"): their time as the platform
 * counted it -- ready, wrapping up, paused by reason -- and their calls.
 */
function YourDay({ tenantId, extensionId, rows, more, endedAt }: {
  tenantId: string; extensionId: string; rows: Cdr[] | undefined; more: boolean; endedAt: number;
}) {
  const [from] = useState(todayStart);
  const att = useQuery({
    queryKey: ['my-attendance', tenantId, extensionId, from, endedAt],
    queryFn: () => api.get<{ data: AgentAttendance[] }>(`/v1/tenants/${tenantId}/agent-attendance?from=${encodeURIComponent(from)}`
      + `&to=${encodeURIComponent(new Date(Date.now() + 60_000).toISOString())}&extension_id=${extensionId}`),
    refetchInterval: 60_000,
  });
  const a = att.data?.data[0];
  const answered = rows?.filter((c) => c.answered_at) ?? [];
  const missed = rows?.filter((c) => !c.answered_at && c.direction !== 'outbound').length ?? 0;
  const talk = answered.reduce((s, c) => s + (c.duration_sec ?? 0), 0);
  const v = (x: number | string) => (rows === undefined ? '…' : x);
  const t = (sec: number | undefined) => (att.data === undefined ? '…' : spell(Math.round(sec ?? 0)));
  return (
    <div>
      {a?.productive_pct != null && (
        <p className="arc-subtle" style={{ margin: 0, padding: '12px 16px 0', fontSize: 12.5 }}>
          {Math.round(a.productive_pct)}% of your time logged in was productive.
        </p>
      )}
      <dl className="arc-ws-today arc-ws-today-4">
        <div><dt>Calls</dt><dd className="arc-num">{v(`${rows?.length ?? 0}${more ? '+' : ''}`)}</dd></div>
        <div><dt>Answered</dt><dd className="arc-num">{v(answered.length)}</dd></div>
        <div><dt>Missed</dt><dd className="arc-num" data-warn={missed > 0 || undefined}>{v(missed)}</dd></div>
        <div><dt>Talk time</dt><dd className="arc-num">{v(formatDuration(talk))}</dd></div>
        <div><dt>Logged in</dt><dd className="arc-num">{t(a?.logged_on_sec)}</dd></div>
        <div><dt>Ready</dt><dd className="arc-num">{t(a?.ready_sec)}</dd></div>
        <div><dt>Wrapping up</dt><dd className="arc-num">{t(a?.wrap_up_sec)}</dd></div>
        <div><dt>Paused</dt><dd className="arc-num">{t(a?.paused_sec)}</dd></div>
      </dl>
      {a && a.by_reason.length > 0 && (
        <div className="arc-card-body arc-row" style={{ gap: 6, flexWrap: 'wrap', borderTop: '1px solid var(--arc-border)' }}>
          {a.by_reason.map((r) => (
            <span key={r.reason} className="arc-pause-chip" data-productive={r.productive || undefined}
              title={r.productive ? 'Counts as work' : 'Does not count as work'}>
              {r.label} · {spell(Math.round(r.seconds))}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** The seat's own calls, newest first: the ten a person actually dials from, and the way to the rest. */
function RecentCalls({ rows, contacts, loading, onDial, canDial, onOpen }: {
  rows: Cdr[];
  contacts: Record<string, PhonebookMatch>;
  loading: boolean;
  onDial: (n: string) => void;
  canDial: boolean;
  onOpen: (id: string) => void;
}) {
  return (
    <div>
      {loading ? (
        <div className="arc-card-body"><div className="arc-skeleton" style={{ height: 120 }} /></div>
      ) : rows.length === 0 ? (
        <Empty icon="phone" title="No calls yet" hint="Calls you make and take appear here." />
      ) : (
        <div className="arc-table-wrap">
          <table className="arc-table arc-ws-recent">
            <thead><tr><th aria-label="Direction" /><th>Who</th><th>When</th><th>Length</th><th>Outcome</th><th>Tags</th><th /></tr></thead>
            <tbody>
              {rows.map((c) => {
                const outbound = c.direction === 'outbound';
                // A withheld caller has no number to ring back, so its call-back is not offered.
                const number = (outbound ? c.to_number : c.from_number) ?? '';
                const who = contacts[number];
                return (
                  <tr key={c.id}>
                    <td className="arc-ws-dir" data-missed={!c.answered_at || undefined}>
                      <Icon name={outbound ? 'callOut' : c.direction === 'internal' ? 'callInternal' : 'callIn'} size={14} />
                    </td>
                    <td>
                      <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
                        <span className="arc-truncate" style={{ fontWeight: 550 }}>
                          {who?.name ?? (number ? readable(number) : 'Withheld')}
                        </span>
                        {who && <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{readable(number)}{who.company ? ` · ${who.company}` : ''}</span>}
                      </div>
                    </td>
                    <td className="arc-subtle">{relativeTime(c.started_at)}</td>
                    <td className="arc-num">
                      {c.answered_at ? formatDuration(c.duration_sec ?? 0) : <Badge tone="muted">{outbound ? 'no answer' : 'missed'}</Badge>}
                    </td>
                    {/* What the call was, in a column of its own (owner, 3 October 2026). */}
                    <td><Outcome d={c.disposition} /></td>
                    <td><TagList tags={c.tags} /></td>
                    <td className="arc-td-right">
                      <span className="arc-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {/* The call to work on: recording, transcript, outcome, tags and notes. */}
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" title="Notes, tags and outcome"
                          aria-label="Notes, tags and outcome" onClick={() => onOpen(c.id)}>
                          <Icon name="edit" size={14} />
                        </button>
                        <button type="button" className="arc-btn arc-btn-sm" title={`Call ${number}`}
                          disabled={!canDial || !number} onClick={() => onDial(number)}>
                          <Icon name="phone" size={13} /> Call
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="arc-card-body" style={{ paddingTop: 10, paddingBottom: 12 }}>
        <Link to="/my/calls" className="arc-btn arc-btn-ghost arc-btn-sm">All your calls</Link>
      </div>
    </div>
  );
}

/** A call's tags, small, in a list's column; a dash when it has none. */
export function TagList({ tags }: { tags: string[] | null | undefined }) {
  if (!tags?.length) return <span className="arc-subtle">—</span>;
  return <span className="arc-taglist">{tags.map((t) => <span key={t} className="arc-taglet">{t}</span>)}</span>;
}

/** A call's outcome as a word with its group's colour beside it, or a dash. */
export function Outcome({ d }: { d: Cdr['disposition'] | null | undefined }) {
  if (!d) return <span className="arc-subtle">—</span>;
  return (
    <span className="arc-outcome" data-kind={d.kind} title={d.note ?? undefined}>
      <span className="arc-outcome-dot" aria-hidden="true" />{d.label}
    </span>
  );
}
